import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginModal from "./components/LoginModal";
import { FeatureFeedbackButton } from "./components/FeedbackWidgets";
import useAnalytics from "./hooks/useAnalytics";
import HomePage from "./pages/HomePage";
import VenueReportPage from "./pages/VenueReportPage";
import ComparePage from "./pages/ComparePage";
import CommunityPage from "./pages/CommunityPage";
import ShortlistPage from "./pages/ShortlistPage";
import ExperiencesPage from "./pages/ExperiencesPage";
import AdminPage from "./pages/AdminPage";
import TrendingVenuesPage from "./pages/TrendingVenuesPage";
import VenueStoryPage from "./pages/VenueStoryPage";
import HomeLanding from "./pages/HomeLanding";
import BrowseVenuesPage from "./pages/BrowseVenuesPage";
import OwnerPortalPage from "./pages/OwnerPortalPage";
import "./App.css";

// Auth callback handler component - handles Google OAuth redirect
function AuthCallbackHandler() {
  const { processGoogleCallback, isAuthenticating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  useEffect(() => {
    const hash = window.location.hash;
    
    // Only process if we have a session_id and haven't processed yet
    if (hash.includes('session_id=') && !hasProcessed.current) {
      hasProcessed.current = true;
      setIsProcessing(true);
      
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        // Clear the hash immediately to prevent re-processing
        window.history.replaceState(null, '', location.pathname || '/');
        
        // Process the callback
        processGoogleCallback(sessionId).then(() => {
          setIsProcessing(false);
          // Force a re-render by navigating to the same path
          navigate(location.pathname || '/', { replace: true });
        }).catch(() => {
          setIsProcessing(false);
          navigate(location.pathname || '/', { replace: true });
        });
      }
    }
  }, []); // Empty dependency array - run only once on mount

  // Show loading state while processing auth
  if (isProcessing || isAuthenticating) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Completing login...</p>
        </div>
      </div>
    );
  }

  return null;
}

// Analytics wrapper component
function AnalyticsWrapper({ children }) {
  useAnalytics(); // Initialize analytics tracking
  return children;
}

// Router component
function AppRouter() {
  const { isLoading } = useAuth();
  
  // Show loading while auth state is being validated
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-stone-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AnalyticsWrapper>
      <AuthCallbackHandler />
      <Routes>
        <Route path="/" element={<HomeLanding />} />
        <Route path="/intelligence" element={<HomePage />} />
        <Route path="/venues" element={<BrowseVenuesPage />} />
        <Route path="/report/:reportId" element={<VenueReportPage />} />
        <Route path="/report" element={<VenueReportPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/shortlist" element={<ShortlistPage />} />
        <Route path="/experiences" element={<ExperiencesPage />} />
        <Route path="/venue-visit-reports" element={<ExperiencesPage />} />
        <Route path="/trending" element={<TrendingVenuesPage />} />
        <Route path="/venues/:slug" element={<VenueStoryPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/owner" element={<OwnerPortalPage />} />
      </Routes>
      <LoginModal />
      <FeatureFeedbackButton />
    </AnalyticsWrapper>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#FDFCF8]">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#292524',
            color: '#FDFCF8',
            border: 'none',
          },
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
